import { useState } from "react";
import type { FormEvent } from "react";
import SectionHeader from "@/components/base/SectionHeader";

const SUBMIT_URL = "https://readdy.ai/api/form/da28th5v7gurs51ef98g";

const categoryOptions = [
  "娱乐八卦",
  "明星动态",
  "影视花絮",
  "综艺名场面",
  "幕后探班",
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("已收到你的留言，我们会尽快联系你！");
      form.reset();
      return;
    }

    setStatus("loading");
    setMessage("");

    const payload: Record<string, string> = {};
    data.forEach((value, key) => {
      if (key !== "website_alt" && typeof value === "string" && value.trim()) {
        payload[key] = value.trim();
      }
    });

    try {
      const body = new URLSearchParams(payload);
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      const ok = res.ok && parsed?.code === "OK";

      if (ok) {
        setStatus("success");
        setMessage("提交成功，感谢你的订阅与反馈，我们会尽快处理！");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg && !serverMsg.includes("spam") ? serverMsg : "提交失败，请稍后再试或更换内容。");
      }
    } catch {
      setStatus("error");
      setMessage("网络开小差了，请稍后再试。");
    }
  };

  return (
    <section id="contact" className="w-full bg-background-100/60 px-4 py-14 md:px-6 md:py-20">
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="flex flex-col lg:col-span-5">
          <SectionHeader
            eyebrow="联系与订阅"
            title="留言反馈 · 订阅吃瓜速递"
            subtitle="想吐槽、想建议、想追更？把需求告诉我们，编辑团队会逐条查看，并在后续更新中优先处理。"
          />
          <div className="mt-8 flex flex-col gap-4">
            {[
              { icon: "ri-mail-line", label: "邮箱", value: "hello@chigua-video.cn" },
              { icon: "ri-time-line", label: "回复时间", value: "工作日 10:00 - 19:00" },
              { icon: "ri-flashlight-line", label: "更新频率", value: "热门板块每日更新" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-4 rounded-xl border border-background-200/70 bg-background-50 p-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <div>
                  <p className="text-xs text-foreground-500">{item.label}</p>
                  <p className="text-sm font-bold text-foreground-950">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <form
          data-readdy-form
          onSubmit={onSubmit}
          className="flex flex-col gap-5 rounded-2xl border border-background-200/70 bg-background-50 p-6 md:p-8 lg:col-span-7"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-foreground-800">你的称呼</span>
              <input
                type="text"
                name="name"
                required
                placeholder="怎么称呼你"
                className="rounded-lg bg-background-100/70 px-4 py-3 text-sm text-foreground-800 outline-none ring-1 ring-background-300/60 placeholder:text-foreground-500 focus:ring-2 focus:ring-primary-400"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-foreground-800">邮箱地址</span>
              <input
                type="email"
                name="email"
                required
                placeholder="用于接收订阅与回复"
                className="rounded-lg bg-background-100/70 px-4 py-3 text-sm text-foreground-800 outline-none ring-1 ring-background-300/60 placeholder:text-foreground-500 focus:ring-2 focus:ring-primary-400"
              />
            </label>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground-800">最常看的板块</span>
            <select
              name="category"
              className="rounded-lg bg-background-100/70 px-4 py-3 text-sm text-foreground-800 outline-none ring-1 ring-background-300/60 focus:ring-2 focus:ring-primary-400"
            >
              {categoryOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="flex items-center justify-between text-sm font-medium text-foreground-800">
              留言内容
              <span className="text-xs font-normal text-foreground-500">最多 500 字</span>
            </span>
            <textarea
              name="message"
              required
              rows={4}
              maxLength={500}
              placeholder="想看的类型、希望增加的内容、遇到的问题…"
              className="resize-none rounded-lg bg-background-100/70 px-4 py-3 text-sm leading-relaxed text-foreground-800 outline-none ring-1 ring-background-300/60 placeholder:text-foreground-500 focus:ring-2 focus:ring-primary-400"
            />
          </label>

          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="opt-in-aux"
          />

          {status === "error" && message && (
            <div className="flex items-center gap-2 rounded-lg bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
              <i className="ri-error-warning-line text-base" />
              {message}
            </div>
          )}
          {status === "success" && message && (
            <div className="flex items-center gap-2 rounded-lg bg-primary-100 px-4 py-3 text-sm text-primary-800">
              <i className="ri-checkbox-circle-line text-base" />
              {message}
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={status === "loading"}
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-fill"} />
              {status === "loading" ? "提交中…" : "提交留言"}
            </button>
            <span className="text-xs text-foreground-500">提交即视为同意我们仅用于回复与订阅推送</span>
          </div>
        </form>
      </div>
    </section>
  );
}